import { useEffect, useRef, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import styles from './Style.module.css';
import { BtnLink } from "../../../Components/Buttons/BtnLink";
import { useGlobalContext } from "../../../Context/GlobalContext";
import { setCalendarFilters } from "../../../APICalling/APICalling";
import { defaultStatus, withDefaultStatus } from "../../../Utils/Constants/defaultStatus";
import Placeholder from "../../../Components/Placeholder/Placeholder";
import SearchNotFoundIcon from "../../../Components/Icons/SearchNotFoundIcon";
import { toast } from "react-toastify";
import logger from "../../../Utils/logger";

const FilterPanel = forwardRef(({ 
  filters = { gender: [], status: [], staffIds: [], roomItem: [], roomIds: [] }, 
  onFilterChange, 
  onClose, 
  onItemIdsChange, 
  onRoomIdsChange 
}, ref) => {
  const { 
    loggedUser,
    isRoomFilterActive, 
    setIsRoomFilterActive,
    homeRoomList,
    selectedOutlet,
    selectedRoomFilter,
    setSelectedRoomFilter,
    isFilterApplied, setIsFilterApplied,
    bookingFilters,
    homeStaffList,
    selectedServices,
    paxes,
   } = useGlobalContext();
  const panelRef = useRef();
  const prevRoomFilterActiveRef = useRef(isRoomFilterActive);
  const filtersInitializedRef = useRef(false);
  const roomsInitializedRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roomSearchTerm, setRoomSearchTerm] = useState("");
  const [isAllRoomsSelected, setIsAllRoomsSelected] = useState(true);
  const [shouldSaveCalendarFilters, setShouldSaveCalendarFilters] = useState(false);

  // Track if filters have been initialized (either from saved filters or user interaction)
  useEffect(() => {
    // Mark as initialized if filters object exists and staffIds property is defined
    // If staffIds is an array (even if empty), it means filters have been explicitly set
    // This preserves user selections including when all staff are unchecked
    if (filters && filters.staffIds !== undefined && Array.isArray(filters.staffIds)) {
      filtersInitializedRef.current = true;
      return;
    }
    
    // Also mark as initialized if any other filter properties indicate filters have been set
    if (filters && (
      (filters.gender && filters.gender.length > 0) ||
      (filters.status && filters.status.length > 0) ||
      (filters.roomIds && filters.roomIds.length > 0) ||
      (filters.roomItem && filters.roomItem.length > 0)
    )) {
      filtersInitializedRef.current = true;
    }
  }, [filters]);

  // Filter rooms based on a specific filter type (used for auto-selection)
  const getFilteredRoomsForType = useCallback((filterType) => {
    switch (filterType) {
      case "resources":
        return homeRoomList; // Show all rooms
      case "rooms":
        return homeRoomList.filter((room) => room.item_category === "Single Bed");
      case "sofa":
        return homeRoomList.filter((room) => room.item_category === "Sofa");
      case "monkeyChair":
        return homeRoomList.filter((room) => room.item_category === "Monkey Chair");
      default:
        return homeRoomList;
    }
  }, [homeRoomList]);

  // When returning to therapist mode from room mode, ensure staff are selected
  // Only auto-select when actually switching modes AND filters haven't been initialized yet
  useEffect(() => {
    const wasInRoomMode = prevRoomFilterActiveRef.current === true;
    const nowInTherapistMode = !isRoomFilterActive;
    const switchedToTherapistMode = wasInRoomMode && nowInTherapistMode;
    
    // Only auto-select all staff if:
    // 1. We just switched from room mode to therapist mode, AND
    // 2. Filters haven't been initialized yet (no saved filters or previous selections), AND
    // 3. staffIds is empty
    if (switchedToTherapistMode && 
        homeStaffList.length > 0 && 
        !filtersInitializedRef.current &&
        (!filters.staffIds || filters.staffIds.length === 0)) {
      onFilterChange(prev => ({
        ...prev,
        staffIds: homeStaffList.map(staff => staff.id)
      }));
      filtersInitializedRef.current = true;
    }
    
    // Update the ref for next comparison
    prevRoomFilterActiveRef.current = isRoomFilterActive;
  }, [isRoomFilterActive, homeStaffList, filters.staffIds, onFilterChange]);


  useEffect(() => {
    const isInAllTherapistMode = !isRoomFilterActive && (!filters.gender || filters.gender.length === 0);
    
    // Only auto-select all staff if filters haven't been initialized yet (no saved filters)
    // This prevents auto-selecting when user has explicitly unchecked some staff
    if (isInAllTherapistMode && homeStaffList.length > 0 && !filtersInitializedRef.current) {
      const currentHomeStaffIds = homeStaffList.map(s => s.id);
      const selectedStaffIds = filters.staffIds || [];
      
      const allStaffIncluded = currentHomeStaffIds.length > 0 && 
        currentHomeStaffIds.every(id => selectedStaffIds.includes(id));
      
      if (!allStaffIncluded) {
        onFilterChange(prev => ({
          ...prev,
          staffIds: currentHomeStaffIds
        }));
        filtersInitializedRef.current = true;
      }
    }
  }, [homeStaffList, isRoomFilterActive, filters.gender, filters.staffIds, onFilterChange]);

  // Initialize room filter when selectedRoomFilter is set from Home.js (saved filters)
  // Respect saved filters or previous filters - only default to all checked when no filters exist
  useEffect(() => {
    if (isRoomFilterActive && selectedRoomFilter && homeRoomList.length > 0) {
      // Map filter type to room item name
      const roomTypeMap = {
        "resources": "",
        "rooms": "Room", 
        "sofa": "Sofa",
        "monkeyChair": "Monkey Chair"
      };
      const roomItemName = roomTypeMap[selectedRoomFilter];
      
      // Check if there are existing saved filters or previous filters
      // filters.roomIds being undefined means no saved filters exist
      // filters.roomIds being an array (even if empty) means saved filters exist
      const hasExistingFilters = filters.roomIds !== undefined;
      
      // Get filtered rooms for the selected room type
      const filteredRooms = getFilteredRoomsForType(selectedRoomFilter);
      const allFilteredRoomIds = filteredRooms.map(room => room.room_id);
      
      // Get current room IDs from filters
      const currentRoomIds = filters.roomIds || [];
      
      // Only initialize if rooms haven't been manually initialized yet
      if (!roomsInitializedRef.current) {
        if (hasExistingFilters) {
          // Use existing saved filters - respect what was saved (even if all unchecked)
          // Update roomItem if not already set
          if (roomItemName && (!filters.roomItem || filters.roomItem.length === 0)) {
            onFilterChange((prev) => ({
              ...prev,
              roomItem: [roomItemName],
            }));
          }
          
          // Update isAllRoomsSelected based on current state
          // If all rooms are unchecked (empty array), isAllSelected should be false
          // If some or all rooms are checked, check if all are selected
          const isAllSelected = currentRoomIds.length > 0 && 
            allFilteredRoomIds.length > 0 &&
            allFilteredRoomIds.every(roomId => currentRoomIds.includes(roomId));
          setIsAllRoomsSelected(isAllSelected);
        } else {
          // No saved filters exist - default to all rooms checked
          const allRoomIds = filteredRooms.map(room => room.room_id);
          
          onFilterChange((prev) => ({
            ...prev,
            roomIds: allRoomIds,
            roomItem: roomItemName ? [roomItemName] : [],
            staffIds: [], // Clear staff IDs when in room mode
          }));
          setIsAllRoomsSelected(true);
        }
        roomsInitializedRef.current = true;
      } else if (hasExistingFilters && roomsInitializedRef.current) {
        // Panel reopened - update isAllRoomsSelected to reflect current saved state
        const isAllSelected = currentRoomIds.length > 0 && 
          allFilteredRoomIds.length > 0 &&
          allFilteredRoomIds.every(roomId => currentRoomIds.includes(roomId));
        setIsAllRoomsSelected(isAllSelected);
      }
    }
  }, [isRoomFilterActive, selectedRoomFilter, homeRoomList, filters.roomIds, filters.roomItem, onFilterChange, getFilteredRoomsForType]);

  // Helper function to clear staff filters
  const clearStaffFilters = () => {
    onFilterChange((prev) => ({
      ...prev,
      gender: [],
      staffIds: [],
    }));
  };

  // Helper function to clear room filters
  const clearRoomFilters = () => {
    setIsRoomFilterActive(false);
    setSelectedRoomFilter("");
    setIsAllRoomsSelected(false);
    setRoomSearchTerm("");
    roomsInitializedRef.current = false; // Reset when clearing room filters
    onFilterChange((prev) => ({
      ...prev,
      roomIds: [],
      roomItem: [],
    }));
  };

  // Handle All Staff selection
  const handleAllTherapistChange = (checked) => {
    clearRoomFilters(); // Clear room filters when staff filter is selected
    onFilterChange((prev) => ({
      ...prev,
      gender: [], // Clear gender filters
      staffIds: checked
        ? homeStaffList.map((staff) => staff.id)
        : [],
      allTherapist: checked,
    }));
    saveCalendarFilters();
  };

  // Handle gender filter selection
  const handleGenderChange = (gender) => {
    const allDraftServices = [
      ...(Array.isArray(selectedServices) ? selectedServices : []),
      ...(Array.isArray(paxes) ? paxes.flatMap((pax) => Array.isArray(pax?.selectedServices) ? pax.selectedServices : []) : []),
    ];

    const conflictingTherapists = allDraftServices
      .map((service) => {
        const therapistId = Number(service?.therapist ?? service?.therapist_id ?? service?.selected_therapist?.value ?? service?.selected_therapist?.id);

        if (Number.isNaN(therapistId)) return null;

        const therapist = homeStaffList.find((staff) => Number(staff?.id) === therapistId);
        if (!therapist?.gender) return null;

        return therapist.gender.toLowerCase() !== gender ? therapist : null;
      })
      .filter(Boolean);

    if (conflictingTherapists.length > 0) {
      const selectedGenderLabel = gender.charAt(0).toUpperCase() + gender.slice(1);
      const conflictingGenderLabel = gender === "male" ? "Female" : "Male";
      toast.warning(
        `Cannot switch to ${selectedGenderLabel} while a ${conflictingGenderLabel} therapist is selected.`
      );
      return;
    }

    clearRoomFilters(); // Clear room filters when gender is selected
    
    // Get staff of the selected gender
    const staffOfGender = homeStaffList.filter(
      (staff) => staff.gender?.toLowerCase() === gender
    );
    const staffIds = staffOfGender.map((staff) => staff.id);

    onFilterChange((prev) => {
      const current = prev.gender || [];
      const isSelected = current.includes(gender);
      
      if (isSelected) {
        // Deselecting gender - clear all filters
        return {
          ...prev,
          gender: [],
          staffIds: [],
        };
      } else {
        // Selecting gender - show only that gender, clear other genders
        return {
          ...prev,
          gender: [gender],
          staffIds: staffIds,
        };
      }
    });
    saveCalendarFilters();
  };

  // Handle status filter selection
  const handleStatusChange = (status) => {
    onFilterChange((prev) => {
      const current = prev.status || [];
      const isSelected = current.includes(status);
      
      if (isSelected) {
        // If status is in defaultStatus, prevent unchecking
        if (defaultStatus.includes(status)) {
          return prev; // Return unchanged state
        }
        // Remove status from filter (only for non-default statuses)
        return {
          ...prev,
          status: current.filter(s => s !== status),
        };
      } else {
        // Add status to filter
        return {
          ...prev,
          status: [...current, status],
        };
      }
    });
  };

  // Handle room filter selection
  const handleRoomFilterChange = (filterType) => {
    
    // If clicking the same filter while room filter is active, deselect it (return to therapist mode)
    if (isRoomFilterActive && selectedRoomFilter === filterType) {
      setIsRoomFilterActive(false);
      setSelectedRoomFilter("");
      setIsAllRoomsSelected(false);
      roomsInitializedRef.current = false; // Reset when exiting room mode
      
      // When returning to therapist mode, re-select all staff and clear room filters
      onFilterChange((prev) => ({
        ...prev,
        gender: [],
        staffIds: homeStaffList.map((staff) => staff.id),
        roomIds: [],
        roomItem: [],
      }));
    } else {
      // Set the new filter type (entering room mode or switching between room filters)
      // When isRoomFilterActive is false and user clicks on resources/rooms/sofa/monkeyChair, 
      // check ALL rooms that will be returned by getFilteredRooms() after activation
      clearStaffFilters(); // Clear staff filters when room filter is selected
      
      // Map filter type to room item name
      const roomTypeMap = {
        "resources": "",
        "rooms": "Room", 
        "sofa": "Sofa",
        "monkeyChair": "Monkey Chair"
      };
      const roomItemName = roomTypeMap[filterType];
      
      // Get the rooms that getFilteredRooms() will return after isRoomFilterActive becomes true
      // getFilteredRooms() returns getFilteredRoomsForType(selectedRoomFilter) when isRoomFilterActive=true
      // This ensures we're checking exactly the rooms that getFilteredRooms() will show
      const filteredRooms = getFilteredRoomsForType(filterType);
      const allRoomIds = filteredRooms.map(room => room.room_id);
      
      // Set state updates
      setIsRoomFilterActive(true);
      setSelectedRoomFilter(filterType);
      setIsAllRoomsSelected(true);
      
      // When clicking a filter from inactive state, ALWAYS check ALL rooms from getFilteredRooms()
      // This is the core requirement: when isRoomFilterActive is false and user clicks a filter,
      // all rooms returned by getFilteredRooms() must be checked
      if (allRoomIds.length > 0) {
        // Rooms are available - check them all immediately and mark as initialized
        roomsInitializedRef.current = true;
        onFilterChange((prev) => ({
          ...prev,
          roomIds: allRoomIds,
          roomItem: roomItemName ? [roomItemName] : [],
        }));
      } else {
        // Rooms not loaded yet - don't mark as initialized and don't set roomIds
        // This ensures the initialization effect will see no existing filters (roomIds undefined)
        // and will check all rooms when they load
        roomsInitializedRef.current = false;
        onFilterChange((prev) => {
          const { roomIds, ...rest } = prev;
          return {
            ...rest,
            roomItem: roomItemName ? [roomItemName] : [],
          };
        });
      }
    }
    saveCalendarFilters();
  };

  // Keep this callback stable so downstream effects don't fire every render.
  const getFilteredRooms = useCallback(() => {
    if (!isRoomFilterActive) return homeRoomList;
    return getFilteredRoomsForType(selectedRoomFilter);
  }, [isRoomFilterActive, homeRoomList, getFilteredRoomsForType, selectedRoomFilter]);

  // Collect all item_id values from filtered rooms
  const getAllItemIdsFromFilteredRooms = useCallback(() => {
    if (!isRoomFilterActive) return [];
    
    const filteredRooms = getFilteredRooms();
    const selectedRoomIds = filters.roomIds || [];
    
    // Filter rooms to only include selected ones
    const selectedRooms = filteredRooms.filter(room => 
      selectedRoomIds.includes(room.room_id)
    );
    
    // Collect all item_id values from all items in all selected rooms
    const allItemIds = selectedRooms.reduce((itemIds, room) => {
      if (room.items && Array.isArray(room.items)) {
        const roomItemIds = room.items.map(item => item.item_id).filter(id => id !== undefined && id !== null);
        return [...itemIds, ...roomItemIds];
      }
      return itemIds;
    }, []);
    
    return allItemIds;
  }, [isRoomFilterActive, filters.roomIds, getFilteredRooms]);

  // Update "Select All" state when room filter type or selections change
  useEffect(() => {
    if (isRoomFilterActive && selectedRoomFilter) {
      const filteredRooms = getFilteredRoomsForType(selectedRoomFilter).filter((room) => {
        const search = roomSearchTerm.trim().toLowerCase();
        if (!search) return true;
        return room.room_name?.toLowerCase().includes(search);
      });
      
      const currentRoomIds = filters.roomIds || [];
      const allFilteredRoomIds = filteredRooms.map(room => room.room_id);
      const isAllSelected = allFilteredRoomIds.length > 0 && 
        allFilteredRoomIds.every(roomId => currentRoomIds.includes(roomId));
      
      setIsAllRoomsSelected(isAllSelected);
    }
  }, [isRoomFilterActive, selectedRoomFilter, filters.roomIds, roomSearchTerm, getFilteredRoomsForType]);

  // Collect and pass item_ids when room filter is active
  useEffect(() => {
    const itemIds = getAllItemIdsFromFilteredRooms();
    
    // Pass itemIds to parent component for filtering
    if (onItemIdsChange) {
      onItemIdsChange(itemIds);
    }
  }, [getAllItemIdsFromFilteredRooms, onItemIdsChange]);

  // Collect all room_id values from filtered rooms
  const getAllRoomIdsFromFilteredRooms = useCallback(() => {
    if (!isRoomFilterActive) return [];
    
    const selectedRoomIds = filters.roomIds || [];
    
    // Return the selected room IDs (these are the room_ids we want to show)
    return selectedRoomIds;
  }, [isRoomFilterActive, filters.roomIds]);

  // Collect and pass room_ids when room filter is active
  useEffect(() => {
    const roomIds = getAllRoomIdsFromFilteredRooms();
    
    // Pass roomIds to parent component for room card filtering
    if (onRoomIdsChange) {
      onRoomIdsChange(roomIds);
    }
  }, [getAllRoomIdsFromFilteredRooms, onRoomIdsChange]);

  // Handle individual room selection
  const handleRoomSelection = (roomId) => {
    const currentRoomIds = filters.roomIds || [];
    const newSelectedRooms = new Set(currentRoomIds);
    if (newSelectedRooms.has(roomId)) {
      newSelectedRooms.delete(roomId);
    } else {
      newSelectedRooms.add(roomId);
    }
    
    // Mark rooms as initialized when user manually selects/deselects
    roomsInitializedRef.current = true;
    
    // Update bookingFilters with new room selection
    onFilterChange((prev) => ({
      ...prev,
      roomIds: Array.from(newSelectedRooms),
    }));
  };

  // Handle "Select All Rooms" toggle
  const handleSelectAllRooms = (checked) => {
    // Get all rooms from the filtered type (ignore search term for "Select All")
    const allFilteredRooms = getFilteredRooms();
    
    // Mark rooms as initialized when user uses "Select All" toggle
    roomsInitializedRef.current = true;
    if (checked) {
      // When selecting all, get ALL room IDs from the filtered type (not just search results)
      const allRoomIds = allFilteredRooms.map(room => room.room_id);
      onFilterChange((prev) => ({
        ...prev,
        roomIds: allRoomIds,
      }));
    } else {
      // When unchecking, clear all room IDs
      onFilterChange((prev) => ({
        ...prev,
        roomIds: [],
      }));
    }
    setIsAllRoomsSelected(checked);
  };

  // Derive isFilterApplied from current filter values (used for API payload and UI)
  const computedIsFilterApplied = useMemo(() => {
    if (isRoomFilterActive) return true;

    if (filters.gender && filters.gender.length > 0) return true;

    if (filters.status && filters.status.length > 0) {
      const hasNonDefaultStatus = filters.status.some(s => !defaultStatus.includes(s));
      if (hasNonDefaultStatus) return true;
    }

    if (homeStaffList.length > 0) {
      const selectedStaffIdSet = new Set((filters.staffIds || []).map((id) => String(id)));
      const allStaffSelected =
        homeStaffList.length > 0 &&
        homeStaffList.every((staff) => selectedStaffIdSet.has(String(staff.id)));

      if (!allStaffSelected) return true;
    }

    return false;
  }, [isRoomFilterActive, filters.gender, filters.status, filters.staffIds, homeStaffList]);

  useEffect(() => {
    setIsFilterApplied(computedIsFilterApplied);
  }, [computedIsFilterApplied, setIsFilterApplied]);

  // Check if all staff is selected (and no room filter is active)
  // Should be true when in "All Therapist" mode AND all current staff are in staffIds
  const isAllStaffSelected = useMemo(() => {
    if (isRoomFilterActive || (filters.gender && filters.gender.length > 0)) {
      return false;
    }
    
    // Check if all current staff are in staffIds
    if (homeStaffList.length === 0) return true;
    
    const selectedStaffIdSet = new Set((filters.staffIds || []).map((id) => String(id)));
    
    // All staff are selected if every staff ID is in the filters
    return homeStaffList.every((staff) => selectedStaffIdSet.has(String(staff.id)));
  }, [isRoomFilterActive, filters.gender, filters.staffIds, homeStaffList]);

  // Gender filter
  const genderFiltered = homeStaffList.filter((staff) => {
    if (!filters.gender || filters.gender.length === 0) return true;
    return filters.gender.includes(staff.gender?.toLowerCase());
  });

  // Search by therapist name / alias / pager number
  const filteredStaffList = genderFiltered.filter((staff) => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return true;
    return (
      staff.name?.toLowerCase().includes(search) ||
      staff.alias?.toLowerCase().includes(search) ||
      staff.pager_number?.toString().toLowerCase().includes(search)
    );
  });


  const filterData = useMemo(() => {
    const filtersArray = {
      gender: [],
      status: [],
      staffIds: [],
      roomItem: [],
      roomIds: [],
      allTherapist: isAllStaffSelected,
      isFilterApplied: computedIsFilterApplied,
    };
    
    // Handle gender filters with checked staff IDs
    if (filters.gender && filters.gender.length > 0) {
      // Get the selected gender (should be only one)
      const selectedGender = filters.gender[0];
      const capitalizedGender = selectedGender.toLowerCase();
      
      // Add gender and checked staff IDs
      filtersArray.gender = [capitalizedGender];
      filtersArray.staffIds = filters.staffIds;
    } else if (!isRoomFilterActive && (filters.staffIds && filters.staffIds.length > 0)) {
      // "All Therapist" is selected (no gender filter, but staff are selected)
      // Add checked staff IDs (no gender filter)
      filtersArray.staffIds = filters.staffIds;
    }
    
    // Add room type filters when in room mode
    if (isRoomFilterActive && selectedRoomFilter) {
      const roomTypeMap = {
        "resources": "",
        "rooms": "Room", 
        "sofa": "Sofa",
        "monkeyChair": "Monkey Chair"
      };
      
      // Add room type to gender field and checked room IDs
      const roomType = roomTypeMap[selectedRoomFilter];
      if (roomType) {
        filtersArray.roomItem = [roomType];
      }
      filtersArray.roomIds = filters.roomIds || [];
    }

    // Always include default statuses; keep Cancelled / No-show if selected
    filtersArray.status = withDefaultStatus(filters.status);
    
    return {
      outlet_id: selectedOutlet,
      user_id: loggedUser,
      mode: isRoomFilterActive ? "room" : "therapist",
      filters: filtersArray,
    };
  }, [selectedOutlet, loggedUser, isRoomFilterActive, selectedRoomFilter, filters.gender, filters.status, filters.staffIds, filters.roomIds, computedIsFilterApplied, isAllStaffSelected]);

  const filterDataRef = useRef(filterData);
  filterDataRef.current = filterData;
  const isSavingRef = useRef(false);

  const persistCalendarFilters = useCallback(async () => {
    if (isSavingRef.current) return false;
    isSavingRef.current = true;
    try {
      const response = await setCalendarFilters(filterDataRef.current);
      if (!response?.success) {
        console.error(response?.message);
      }
      return response?.success ?? false;
    } catch (error) {
      console.error(error?.message);
      return false;
    } finally {
      isSavingRef.current = false;
    }
  }, []);

  // Trigger save after state/props updates are reflected in filterData.
  const saveCalendarFilters = useCallback(() => {
    setShouldSaveCalendarFilters(true);
  }, []);

  useEffect(() => {
    if (!shouldSaveCalendarFilters) return;

    setShouldSaveCalendarFilters(false);
    persistCalendarFilters();
  }, [shouldSaveCalendarFilters, filterData, persistCalendarFilters]);

  // Expose save methods via ref (saveAndClose survives unmount for calendar clicks)
  useImperativeHandle(ref, () => ({
    saveCalendarFilters,
    saveAndClose: persistCalendarFilters,
  }));

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !e.target.closest(`.${styles.FilterBtn}`)
      ) {
        persistCalendarFilters();
        onClose();
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [persistCalendarFilters, onClose]);

  return (
    <div ref={panelRef} className={`${styles.filterCard} prevent-outside-click`}>
      <div className={styles.filterCardBody}>

        {/* Gender + All Therapist Filter */}
        <div className={`${styles.card} ${styles.therapist}`}>
          <h6 className={`${styles.heading} ${styles.space}`}>
            Show by group (Person who is on duty)
          </h6>

          {/* All Therapist */}
          <div className={styles.customCheckbox}>
            <label className={`${styles.li} d-flex justify-between checkbox v2`}>
              <input
                type="radio"
                checked={isAllStaffSelected}
                className={styles.hiddenCheckbox}
                onChange={(e) => handleAllTherapistChange(e.target.checked)}
              />
              <h6 className={`${styles.heading} ${styles.light}`}>All Therapist</h6>
              <span className="checkmark icn check" />
            </label>
          </div>

          {/* Gender filters */}
          {["male", "female"].map((gender) => (
            <div key={gender} className={`${styles.customCheckbox} ${styles.therapistGender}`}>
              <label className={`${styles.li} d-flex justify-between checkbox v2`}>
                <input
                  type="radio"
                  checked={(filters.gender || []).includes(gender)}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleGenderChange(gender)}
                />
                <h6 className={`${styles.heading} ${styles.light}`}>
                  {gender.charAt(0).toUpperCase() + gender.slice(1)}
                </h6>
                <span className="checkmark icn check" />
              </label>
            </div>
          ))}
        </div>

        <div className={styles.hr} />

        {/* Rooms/Resources/Sofa section */}
        <div className={`${styles.card} ${styles.room}`}>
          <div className={styles.customCheckbox}>
            <label className={`${styles.li} d-flex justify-between checkbox v2`}>
              <input
                type="radio"
                checked={isRoomFilterActive && (!filters.roomItem || filters.roomItem.length === 0)}
                className={styles.hiddenCheckbox}
                onChange={() => handleRoomFilterChange("resources")}
              />
              <h6 className={`${styles.heading} ${styles.light}`}>Resources</h6>
              <span className="checkmark icn check" />
            </label>
          </div>

          <div className={styles.customCheckbox}>
            <label className={`${styles.li} d-flex justify-between checkbox v2`}>
              <input
                type="radio"
                checked={isRoomFilterActive && filters.roomItem && filters.roomItem.includes('Room')}
                className={styles.hiddenCheckbox}
                onChange={() => handleRoomFilterChange("rooms")}
              />
              <h6 className={`${styles.heading} ${styles.light}`}>Rooms</h6>
              <span className="checkmark icn check" />
            </label>
          </div>

          <div className={styles.customCheckbox}>
            <label className={`${styles.li} d-flex justify-between checkbox v2`}>
              <input
                type="radio"
                checked={isRoomFilterActive && filters.roomItem && filters.roomItem.includes('Sofa')}
                className={styles.hiddenCheckbox}
                onChange={() => handleRoomFilterChange("sofa")}
              />
              <h6 className={`${styles.heading} ${styles.light}`}>Sofa</h6>
              <span className="checkmark icn check" />
            </label>
          </div>

          <div className={styles.customCheckbox}>
            <label className={`${styles.li} d-flex justify-between checkbox v2`}>
              <input
                type="radio"
                checked={isRoomFilterActive && filters.roomItem && filters.roomItem.includes('Monkey Chair')}
                className={styles.hiddenCheckbox}
                onChange={() => handleRoomFilterChange("monkeyChair")}
              />
              <h6 className={`${styles.heading} ${styles.light}`}>Monkey Chair</h6>
              <span className="checkmark icn check" />
            </label>
          </div>
        </div>

        <div className={styles.hr} />

        <div className={`${styles.card} ${styles.bookingStatus}`}>
          <h6 className={`${styles.heading} ${styles.space}`}>Booking Status</h6>
          <div className={styles.rw}>
            <div className={styles.customCheckbox}>
              <label className={`${styles.li} d-flex checkbox ${defaultStatus.includes('Confirmed') && (filters.status || []).includes('Confirmed') ? styles.notSelectable : ''}`}>
                <input
                  type="checkbox"
                  checked={(filters.status || []).includes('Confirmed')}
                   disabled={defaultStatus.includes('Confirmed') && (filters.status || []).includes('Confirmed')}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleStatusChange('Confirmed')}
                />
                <span className="checkmark icn check" />
                <h6 className={`${styles.heading} ${styles.light}`}>Confirmed <span className="status-circle confirmed"></span></h6>
              </label>
            </div>
            <div className={styles.customCheckbox}>
              <label className={`${styles.li} d-flex checkbox ${defaultStatus.includes('Unconfirmed') && (filters.status || []).includes('Unconfirmed') ? styles.notSelectable : ''}`}>
                <input
                  type="checkbox"
                  checked={(filters.status || []).includes('Unconfirmed')}
                 disabled={defaultStatus.includes('Unconfirmed') && (filters.status || []).includes('Unconfirmed')}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleStatusChange('Unconfirmed')}
                />
                <span className="checkmark icn check" />
                <h6 className={`${styles.heading} ${styles.light}`}>Unconfirmed <span className="status-circle unconfirmed"></span></h6>
              </label>
            </div>
            <div className={styles.customCheckbox}>
              <label className={`${styles.li} d-flex checkbox ${defaultStatus.includes('Checked-in') && (filters.status || []).includes('Checked-in') ? styles.notSelectable : ''}`}>
                <input
                  type="checkbox"
                  checked={(filters.status || []).includes('Checked-in')}
                  disabled={defaultStatus.includes('Checked-in') && (filters.status || []).includes('Checked-in')}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleStatusChange('Checked-in')}
                />
                <span className="checkmark icn check" />
                <h6 className={`${styles.heading} ${styles.light}`}>Checked In <span className="status-circle checked-in"></span></h6>
              </label>
            </div>
            <div className={styles.customCheckbox}>
              <label className={`${styles.li} d-flex checkbox ${defaultStatus.includes('Completed') && (filters.status || []).includes('Completed') ? styles.notSelectable : ''}`}>
                <input
                  type="checkbox"
                  checked={(filters.status || []).includes('Completed')}
                  disabled={defaultStatus.includes('Completed') && (filters.status || []).includes('Completed')}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleStatusChange('Completed')}
                />
                <span className="checkmark icn check" />
                <h6 className={`${styles.heading} ${styles.light}`}>Completed <span className="status-circle completed"></span></h6>
              </label>
            </div>
            <div className={styles.customCheckbox}>
              <label className={`${styles.li} d-flex checkbox ${defaultStatus.includes('Cancelled') && (filters.status || []).includes('Cancelled') ? styles.notSelectable : ''}`}>
                <input
                  type="checkbox"
                  checked={(filters.status || []).includes('Cancelled')}
                  disabled={defaultStatus.includes('Cancelled') && (filters.status || []).includes('Cancelled')}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleStatusChange('Cancelled')}
                />
                <span className="checkmark icn check" />
                <h6 className={`${styles.heading} ${styles.light}`}>Cancelled <span className="status-circle cancelled"></span></h6>
              </label>
            </div>
            <div className={styles.customCheckbox}>
              <label className={`${styles.li} d-flex checkbox ${defaultStatus.includes('No-show') && (filters.status || []).includes('No-show') ? styles.notSelectable : ''}`}>
                <input
                  type="checkbox"
                  checked={(filters.status || []).includes('No-show')}
                  disabled={defaultStatus.includes('No-show') && (filters.status || []).includes('No-show')}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleStatusChange('No-show')}
                />
                <span className="checkmark icn check" />
                <h6 className={`${styles.heading} ${styles.light}`}>No Show <span className="status-circle noshow"></span></h6>
              </label>
            </div>
            
            <div className={styles.customCheckbox}>
              <label className={`${styles.li} d-flex checkbox ${defaultStatus.includes('Check-in (In Progress)') && (filters.status || []).includes('Check-in (In Progress)') ? styles.notSelectable : ''}`}>
                <input
                  type="checkbox"
                  checked={(filters.status || []).includes('Check-in (In Progress)')}
                  disabled={defaultStatus.includes('Check-in (In Progress)') && (filters.status || []).includes('Check-in (In Progress)')}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleStatusChange('Check-in (In Progress)')}
                />
                <span className="checkmark icn check" />
                <h6 className={`${styles.heading} ${styles.light}`}>Check-in (In Progress) <span className="status-circle checkininprogress"></span></h6>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.hr} />
	
        {isRoomFilterActive ? 
        <div className={styles.ResourceCard}>
          <div className={`${styles.card} ${styles.space} d-flex justify-between`}>
            <h6 className={styles.heading}>Select Resource</h6>
              <div className={`${styles.selectAll} ${styles.gap5} d-flex`}>
                <h6 className={styles.heading}>Select All</h6>
                <div className={styles.customCheckbox}>
                <label className="checkbox">
                  <input
                  type="checkbox"
                  checked={isAllRoomsSelected}
                  className={styles.hiddenCheckbox}
                  onChange={(e) => handleSelectAllRooms(e.target.checked)}
                  />
                  <span className="checkmark icn check" />
                </label>
              </div>
            </div>
          </div>
      
          {/* Search box */}
          <div className={`${styles.searchBox} ${styles.space}`}>
          <input
            className="form-control"
            placeholder="Search by room name"
            value={roomSearchTerm}
            onChange={(e) => setRoomSearchTerm(e.target.value)}
          />
          </div>
      
          {/* Filtered room list */}
          <div className={styles.homeStaffList}>
          {(() => {
            const filteredRooms = getFilteredRooms()
              .filter((room) => {
                const search = roomSearchTerm.trim().toLowerCase();
                if (!search) return true;
                return room.room_name?.toLowerCase().includes(search);
              });
            
            return filteredRooms.length === 0 ? (
              <div className={`${styles.space} ${styles.heading} ${styles.light}`}>
                <div className={styles.noResults}>
                  <Placeholder
                    icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
                    heading="Room not found"
                    para="Please check the room name/no. or try with different room name/no."
                  />
                </div>
              </div>
            ) : (
              filteredRooms.map((room) => (
                <label 
                key={room.room_id} 
                title={room.room_name ? room.room_name : 'N/A'}
                className={`${styles.li} d-flex justify-between align-center checkbox`} 
                onClick={(e) => e.stopPropagation()}
                >
                  <input
                  type="checkbox"
                  checked={(filters.roomIds || []).includes(room.room_id)}
                  className={styles.hiddenCheckbox}
                  onChange={() => handleRoomSelection(room.room_id)}
                  />
                  {room.room_name ? room.room_name : 'N/A'}
                  <span className="checkmark icn check" />
                </label>
              ))
            );
          })()}
          </div>
          </div> 
          : 
          <div className={styles.therapistCard}>
            <div className={`${styles.card} ${styles.space} d-flex justify-between`}>
            <h6 className={styles.heading}>Select Therapist</h6>
            <div className={`${styles.selectAll} ${styles.gap5} d-flex`}>
              <h6 className={styles.heading}>Select All</h6>
              <div className={styles.customCheckbox}>
                <label className="checkbox">
                  <input
                  type="checkbox"
                  checked={
                    filteredStaffList.length > 0 &&
                    filteredStaffList.every((staff) =>
                      (filters.staffIds || []).some((id) => String(id) === String(staff.id))
                    )
                  }
                  className={styles.hiddenCheckbox}
                  onChange={(e) => {
                    const filteredIds = filteredStaffList.map((staff) => staff.id);
                    const currentStaffIds = filters.staffIds || [];
                    
                    // Check if all filtered staff are currently selected
                    const allFilteredSelected = filteredIds.length > 0 && 
                      filteredIds.every((id) =>
                        currentStaffIds.some((currentId) => String(currentId) === String(id))
                      );
                    
                    if (allFilteredSelected) {
                      // All filtered staff are selected → uncheck all (remove filtered IDs)
                      const filteredIdSet = new Set(filteredIds.map((id) => String(id)));
                      const updatedIds = currentStaffIds.filter(
                        (id) => !filteredIdSet.has(String(id))
                      );
                      onFilterChange((prev) => ({
                        ...prev,
                        staffIds: updatedIds,
                        allTherapist: false,
                      }));
                    } else {
                      // Not all filtered staff are selected → check all (add all filtered IDs)
                      const updatedIds = [...new Set([...currentStaffIds, ...filteredIds])];
                      const updatedIdSet = new Set(updatedIds.map((id) => String(id)));
                      const allTherapist =
                        (!filters.gender || filters.gender.length === 0) &&
                        homeStaffList.length > 0 &&
                        homeStaffList.every((s) => updatedIdSet.has(String(s.id)));
                      onFilterChange((prev) => ({
                        ...prev,
                        staffIds: updatedIds,
                        allTherapist,
                      }));
                    }
                  }}
                  />
                  <span className="checkmark icn check" />
                </label>
              </div>
            </div>
            </div>
      
            {/* Search box */}
            <div className={`${styles.searchBox} ${styles.space}`}>
            <input
              className="form-control"
              placeholder="Search by therapist"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            </div>
      
            {/* Filtered staff list */}
            <div className={styles.homeStaffList}>
            {filteredStaffList.length === 0 ? (
              <div className={`${styles.space} ${styles.heading} ${styles.light}`}>
                <div className={styles.noResults}>
                  <Placeholder
                      icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
                      heading="Therapist not found"
                      para="Please check the name or try with different name"
                  />
							</div>
              </div>
            ) : (
              filteredStaffList.map((staff) => {
                // Always derive checked state from selected staff IDs.
                // Auto-population of "all therapist" mode is handled by effects.
                const selectedStaffIdSet = new Set((filters.staffIds || []).map((id) => String(id)));
                const isChecked = selectedStaffIdSet.has(String(staff.id));
                
                const genderColor =
                staff.gender === "female"
                  ? "female"
                  : staff.gender === "male"
                  ? "male"
                  : "";
        
                const toggleStaff = () => {
                  const currentStaffIds = filters.staffIds || [];
                  const updated = isChecked
                    ? currentStaffIds.filter((id) => String(id) !== String(staff.id))
                    : [...currentStaffIds, staff.id];
                  const updatedIdSet = new Set(updated.map((id) => String(id)));
                  const allTherapist =
                    (!filters.gender || filters.gender.length === 0) &&
                    homeStaffList.length > 0 &&
                    homeStaffList.every((s) => updatedIdSet.has(String(s.id)));
                  onFilterChange((prev) => ({
                    ...prev,
                    staffIds: updated,
                    allTherapist,
                  }));
                };
        
                return (
                <div
                  key={staff.id}
                  className={`${styles.li} d-flex justify-between align-center`}
                  onClick={toggleStaff}
                >
                  <div className={`${styles.left} ${styles.gap5} d-flex align-center`}>
                    <div className={`${styles.pager} ${genderColor}`} title={staff.pager_number ? staff.pager_number : 'N/A'}>
                      {staff.pager_number ? staff.pager_number : 'N/A'}
                    </div>
                    <span className={styles.txt} title={staff.alias || staff.name}>
                      {staff.alias || staff.name}
                    </span>
                  </div>
        
                  <div className={`${styles.right} ${styles.customCheckbox}`}>
                  <label className="checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                    type="checkbox"
                    checked={isChecked}
                    className={styles.hiddenCheckbox}
                    onChange={toggleStaff}
                    />
                    <span className="checkmark icn check" />
                  </label>
                  </div>
                </div>
                );
              })
            )}
            </div>
          </div>
        }

      </div>

      {/* Footer */}
      <div className={styles.filterCardFooter}>
        <BtnLink
          text="Clear Filter (Return to Default)"
          title="Clear Filter (Return to Default)"
          className={styles.clearBtn}
          disabled={(bookingFilters?.isFilterApplied === true || isFilterApplied === true) ? false : true}
          handleClick={() => {
            // Get all staff IDs to select all when clearing filters
            const allStaffIds = homeStaffList.length > 0 
              ? homeStaffList.map((staff) => staff.id)
              : [];

            const allRoomIds = homeRoomList.length > 0 
              ? homeRoomList.map((room) => room.room_id)
              : [];
            
            onFilterChange({
              gender: [],
              status: defaultStatus, // Default statuses: all except Cancelled and No-show
              staffIds: allStaffIds,
              roomIds: allRoomIds,
              roomItem: [],
              allTherapist: true,
              isFilterApplied: false,
            });
            setSearchTerm("");
            setRoomSearchTerm("");
            setIsRoomFilterActive(false);
            setSelectedRoomFilter("");
            // setIsAllRoomsSelected(false);
            setIsFilterApplied(false);
            roomsInitializedRef.current = false; // Reset when clearing all filters
            saveCalendarFilters();
          }}
        />
      </div>
    </div>
  );
});

export default FilterPanel;
